import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Logo from './Logo';
import Login from './Login';
import UserManagement from './UserManagement';
import LogsView from './LogsView';
import TokenManagement from './TokenManagement';
import bannerImage from './assets/banner.png';
import { useAuth } from './AuthContext';
import './index.css';

const API_URL = process.env.REACT_APP_API_URL || (window.location.origin.includes('localhost:3000') ? 'http://localhost:3001' : '');

function App() {
  const { user, logout, loading: authLoading, hasPermission } = useAuth();
  const [packs, setPacks] = useState([]);
  const [stats, setStats] = useState({ totalPacks: 0, totalDownloads: 0, totalSize: 0 });
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editingPack, setEditingPack] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [activeTab, setActiveTab] = useState('packs');

  const [uploadForm, setUploadForm] = useState({
    name: '',
    description: '',
    file: null
  });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const loadPacksCallback = useCallback(() => {
    loadPacks();
  }, [sortBy, sortOrder, searchQuery]);

  useEffect(() => {
    if (user) {
      loadPacksCallback();
      loadStats();
    }
  }, [user, loadPacksCallback]);

  const loadPacks = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      params.append('sortBy', sortBy);
      params.append('sortOrder', sortOrder);
      
      const response = await axios.get(`${API_URL}/api/packs?${params}`);
      setPacks(response.data);
      setLoading(false);
    } catch (err) {
      setError('Erreur lors du chargement des packs');
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/stats`);
      setStats(response.data);
    } catch (err) {
      console.error('Erreur lors du chargement des stats:', err);
    }
  };


  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!uploadForm.file) {
      setError('Veuillez sélectionner un fichier');
      return;
    }

    const formData = new FormData();
    formData.append('file', uploadForm.file);
    formData.append('name', uploadForm.name);
    formData.append('description', uploadForm.description);

    try {
      setUploading(true);
      setUploadProgress(0);
      await axios.post(`${API_URL}/api/packs`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            setUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
          }
        }
      });
      setSuccess('Pack uploadé avec succès!');
      setUploadForm({ name: '', description: '', file: null });
      setUploadProgress(0);
      setUploading(false);
      setShowUploadModal(false);
      loadPacks();
      loadStats();
    } catch (err) {
      setUploading(false);
      setUploadProgress(0);
      setError(err.response?.data?.error || 'Erreur lors de l\'upload');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const formData = new FormData();
    if (uploadForm.file) {
      formData.append('file', uploadForm.file);
    }
    formData.append('name', uploadForm.name);
    formData.append('description', uploadForm.description);

    try {
      setUploading(true);
      setUploadProgress(0);
      await axios.put(`${API_URL}/api/packs/${editingPack.id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            setUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
          }
        }
      });
      setSuccess('Pack mis à jour avec succès!');
      setUploading(false);
      setUploadProgress(0);
      setEditingPack(null);
      setUploadForm({ name: '', description: '', file: null });
      loadPacks();
      loadStats();
    } catch (err) {
      setUploading(false);
      setUploadProgress(0);
      setError(err.response?.data?.error || 'Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce pack?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/packs/${id}`);
      setSuccess('Pack supprimé avec succès!');
      loadPacks();
      loadStats();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const openEditModal = (pack) => {
    setEditingPack(pack);
    setUploadForm({
      name: pack.name,
      description: pack.description || '',
      file: null
    });
    setError(null);
    setSuccess(null);
  };

  const closeModal = () => {
    setShowUploadModal(false);
    setEditingPack(null);
    setUploadForm({ name: '', description: '', file: null });
    setError(null);
    setSuccess(null);
    setUploadProgress(0);
    setUploading(false);
  };

  const getDownloadUrl = (packId) => {
    const baseUrl = API_URL || window.location.origin.replace(':3000', ':3001');
    return `${baseUrl}/api/packs/${packId}/download`;
  };

  const getDirectDownloadUrl = (filename) => {
    const baseUrl = API_URL || window.location.origin.replace(':3000', ':3001');
    return `${baseUrl}/uploads/${filename}`;
  };

  const getServerPropertiesSnippet = (pack) => {
    const url = getDownloadUrl(pack.id);
    const sha1 = pack.sha1 || '';
    return `resource-pack=${url}\nresource-pack-sha1=${sha1}`;
  };

  if (authLoading) {
    return <div className="loading">Chargement...</div>;
  }

  if (!user) {
    return <Login />;
  }

  return (
    <div className="container">
      <div className="header">
        <div className="header-banner" style={{ backgroundImage: `url(${bannerImage})` }}></div>
        <div className="header-content">
          <Logo />
          <div className="header-text">
            <h1>EndoPACK</h1>
            <p>Gestionnaire interne de resource packs Minecraft</p>
          </div>
          <div className="header-actions">
            <span className="user-info">{user.username} ({user.role})</span>
            <button className="btn btn-secondary" onClick={logout}>
              Déconnexion
            </button>
          </div>
        </div>
      </div>

      <nav className="main-nav">
        <button
          className={`nav-btn ${activeTab === 'packs' ? 'active' : ''}`}
          onClick={() => setActiveTab('packs')}
        >
          📦 Packs
        </button>
        {hasPermission('users.view') && (
          <button
            className={`nav-btn ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            👥 Utilisateurs
          </button>
        )}
        {hasPermission('logs.view') && (
          <button
            className={`nav-btn ${activeTab === 'logs' ? 'active' : ''}`}
            onClick={() => setActiveTab('logs')}
          >
            📋 Logs
          </button>
        )}
        <button
          className={`nav-btn ${activeTab === 'api' ? 'active' : ''}`}
          onClick={() => setActiveTab('api')}
        >
          🔑 API & Tokens
        </button>
      </nav>

      {activeTab === 'users' && hasPermission('users.view') && (
        <UserManagement onClose={() => setActiveTab('packs')} />
      )}

      {activeTab === 'logs' && hasPermission('logs.view') && (
        <LogsView onClose={() => setActiveTab('packs')} />
      )}

      {activeTab === 'api' && (
        <TokenManagement />
      )}

      {activeTab === 'packs' && (
        <>
          <div className="stats">
            <div className="stat-card">
              <h3>Total Packs</h3>
              <div className="value">{stats.totalPacks || 0}</div>
            </div>
            <div className="stat-card">
              <h3>Total Téléchargements</h3>
              <div className="value">{stats.totalDownloads || 0}</div>
            </div>
            <div className="stat-card">
              <h3>Taille Totale</h3>
              <div className="value">{formatFileSize(stats.totalSize || 0)}</div>
            </div>
          </div>

          <div className="search-sort-section">
            <div className="search-box">
              <input
                type="text"
                placeholder="Rechercher un pack..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
            </div>
            <div className="sort-box">
              <label>Trier par:</label>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="createdAt">Date de création</option>
                <option value="name">Nom</option>
                <option value="downloadCount">Téléchargements</option>
                <option value="fileSize">Taille</option>
                <option value="updatedAt">Dernière modification</option>
              </select>
              <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
                <option value="DESC">Décroissant</option>
                <option value="ASC">Croissant</option>
              </select>
            </div>
          </div>

          {hasPermission('packs.create') && (
            <div className="upload-section">
              <h2>Gestion des Packs</h2>
              <button className="btn btn-primary" onClick={() => setShowUploadModal(true)}>
                + Ajouter un nouveau pack
              </button>
            </div>
          )}

          {loading ? (
        <div className="loading">Chargement...</div>
      ) : packs.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📦</div>
          <h2 className="empty-state-title">Aucun pack disponible</h2>
          <p className="empty-state-text">
            {searchQuery ? 'Aucun résultat pour votre recherche' : 'Commencez par ajouter votre premier resource pack !'}
          </p>
        </div>
      ) : (
        <>
          {packs.length > 0 && (
            <div className="featured-section">
              <h2 className="section-title">En Vedette</h2>
              <p className="section-subtitle">Retrouvez ici vos resource packs</p>
            </div>
          )}
          <div className="packs-grid">
            {packs.map(pack => (
              <div key={pack.id} className="pack-card">
                <div className="pack-card-image">
                  <div className="pack-card-image-placeholder">📦</div>
                </div>
                <div className="pack-card-content">
                  <h3>{pack.name}</h3>
                  <div className="description">
                    {pack.description || <em>Aucune description</em>}
                  </div>
                  <div className="pack-info">
                    <span>📦 {formatFileSize(pack.fileSize)}</span>
                    <span>⬇️ {pack.downloadCount} téléchargements</span>
                  </div>
                  <div className="download-url" style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '10px' }}>
                    <div>
                      <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '2px' }}>Lien API (avec stats):</div>
                      <code style={{ userSelect: 'all', background: '#f5f5f5', padding: '4px', borderRadius: '4px', display: 'block', wordBreak: 'break-all' }}>
                        {getDownloadUrl(pack.id)}
                      </code>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '2px' }}>Lien Direct (.zip):</div>
                      <code style={{ userSelect: 'all', background: '#f5f5f5', padding: '4px', borderRadius: '4px', display: 'block', wordBreak: 'break-all' }}>
                        {getDirectDownloadUrl(pack.filename)}
                      </code>
                    </div>
                  </div>
                  <div className="server-properties-snippet">
                    <pre>{getServerPropertiesSnippet(pack)}</pre>
                  </div>
                  {hasPermission('packs.update') && (
                    <div className="pack-actions">
                      <button className="btn btn-secondary" onClick={() => openEditModal(pack)}>
                        ✏️ Modifier
                      </button>
                      {hasPermission('packs.delete') && (
                        <button className="btn btn-danger" onClick={() => handleDelete(pack.id)}>
                          🗑️ Supprimer
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
          )}
        </>
      )}

      {(showUploadModal || editingPack) && hasPermission('packs.create') && (
        <div className="modal" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingPack ? 'Modifier le pack' : 'Ajouter un nouveau pack'}</h2>
              <button className="close-btn" onClick={closeModal}>×</button>
            </div>
            <form onSubmit={editingPack ? handleUpdate : handleUpload}>
              <div className="form-group">
                <label>Nom du pack</label>
                <input
                  type="text"
                  value={uploadForm.name}
                  onChange={(e) => setUploadForm({ ...uploadForm, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Fichier ZIP {editingPack && '(laisser vide pour ne pas modifier)'}</label>
                <div className="file-input-wrapper">
                  <input
                    type="file"
                    accept=".zip"
                    onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files[0] })}
                    required={!editingPack}
                  />
                  <label className="file-input-label">
                    {uploadForm.file ? uploadForm.file.name : 'Cliquez pour sélectionner un fichier .zip'}
                  </label>
                </div>
              </div>
              {error && <div className="error">{error}</div>}
              {uploading && (
                <div className="progress-bar-container">
                  <div className="progress-bar" style={{ width: `${uploadProgress}%` }}>
                    {uploadProgress}%
                  </div>
                </div>
              )}
              <button type="submit" className="btn btn-primary" style={{ width: '100%', marginTop: '20px' }} disabled={uploading}>
                {uploading ? `Envoi en cours... ${uploadProgress}%` : (editingPack ? 'Mettre à jour' : 'Uploader')}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
